import { Resources } from 'constants/resources';

import { createContext, useContext, useMemo } from 'react';

import { useGetList, useRecordContext } from 'react-admin';
import { Country } from 'types/embedded/country';
import { Currency } from 'types/embedded/currency';
import { GatewayRule } from 'types/embedded/gatewayRule';

import { extractConditionCodes } from '../utils/extractConditionCodes';

type GatewayRuleConditionsContextValue = {
  currenciesByCode: Map<string, Currency>;
  countriesByCode: Map<string, Country>;
};

const GatewayRuleConditionsContext = createContext<GatewayRuleConditionsContextValue>({
  currenciesByCode: new Map(),
  countriesByCode: new Map(),
});

type Props = {
  children: React.ReactNode;
};

export function GatewayRuleConditionsProvider({ children }: Props): JSX.Element {
  const record = useRecordContext<GatewayRule>();

  const { currencyCodes, countryCodes } = extractConditionCodes(record);

  const { data: currencies = [] } = useGetList<Currency>(
    Resources.Currencies,
    {
      pagination: { page: 1, perPage: 100 },
      filter: { alphaCode: currencyCodes },
    },
    { enabled: !!currencyCodes.length },
  );

  const { data: countries = [] } = useGetList<Country>(
    Resources.Countries,
    {
      pagination: { page: 1, perPage: 100 },
      filter: { alphaCode: countryCodes },
    },
    { enabled: !!countryCodes.length },
  );

  const value = useMemo<GatewayRuleConditionsContextValue>(
    () => ({
      currenciesByCode: new Map(currencies.map((currency) => [currency.alphaCode, currency])),
      countriesByCode: new Map(countries.map((country) => [country.alphaCode, country])),
    }),
    [currencies, countries],
  );

  return (
    <GatewayRuleConditionsContext.Provider value={value}>
      {children}
    </GatewayRuleConditionsContext.Provider>
  );
}

export function useGatewayRuleConditions(): GatewayRuleConditionsContextValue {
  return useContext(GatewayRuleConditionsContext);
}
